import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators'
import _ from 'lodash'
import { DataTablePagination, Hospital } from '@/types/_'
import Apis from '@/Apis/_'

type SetOperadoraConvenioModal = {
  name: string,
  value: boolean,
}

type OperadoraConvenioModal = {
  operadora: boolean,
}

interface Operadora {
  operadoraId: string,
  operadoraNome: string,
  grupoHospital?: string,
  registroAns: string,
}

interface Convenio {
  convenioId: string,
  convenioNome: string,
}

interface ConvenioOperadora {
  operadoraId: string,
  operadoraNome: string,
  convenioId: string,
  convenioNome: string,
  operadoraRegistroANS: string,
}

@Module({ namespaced: true, name: 'OperadoraConvenioStore' })
export default class OperadoraConvenioStore extends VuexModule {
  public hospitais: Hospital[] = []
  public hospitalId: string = ''
  public modal: OperadoraConvenioModal = {
    operadora: false,
  }

  public searchConvenio: string = ''

  @Mutation
  setSearchConvenio(val: string) {
    this.searchConvenio = val
  }

  @Mutation
  clearSearchConvenio() {
    this.searchConvenio = ''
  }

  @Mutation
  setModal(modal: SetOperadoraConvenioModal) {
    this.modal[modal.name] = modal.value
  }

  @Mutation
  setHospitais(value: Hospital[]) {
    this.hospitais = value
  }

  @Mutation
  setHospitalId(value: string) {
    this.hospitalId = value
  }

  @Mutation
  clearHospitais() {
    this.hospitais = []
  }

  @Mutation
  clearHospitalId() {
    this.hospitalId = ''
  }

  @Action
  forceCast<T>(obj: any) {
    return obj
  }

  @Action({ commit: 'setHospitais' })
  async getHospitais() {
    const request = await Apis.Agendamento.SuporteApi.getHospitaisByUserCurrent()
    if (request.success) {
      const data = request.data.map((c: any) => {
        const hospital = c
        hospital.id = _.toUpper(c.id)
        hospital.nome = _.toUpper(c.nome)
        return hospital
      })
      const hospitaisCast = await this.forceCast<Hospital[]>(data)
      return hospitaisCast
    }
    return []
  }

  /** Operadoras e convênios */
  private paginationOperadoraConvenioDefault: DataTablePagination = {
    descending: false,
    page: 1,
    rowsPerPage: 10,
    sortBy: null,
    totalItems: 0,
    rowsPerPageItems: [10, 50, 100],
  }

  private conveniosOperadoraDefault: ConvenioOperadora[] = []
  public conveniosOperadora: ConvenioOperadora[] = [...this.conveniosOperadoraDefault]
  public grupoHospitais: string[] = []

  @Mutation
  clearGrupoHospitais() {
    this.grupoHospitais = []
  }

  @Mutation
  setConveniosOperadora(value: ConvenioOperadora[]) {
    this.convenios.forEach((convenio: Convenio) => {
      const c = value.find((e: any) => e.convenioId === convenio.convenioId)
      const el = c !== undefined ? { ...c } : {
        convenioId: convenio.convenioId,
        convenioNome: convenio.convenioNome,
        operadoraRegistroANS: '',
        operadoraId: '',
        operadoraNome: '',
      } as ConvenioOperadora
      this.conveniosOperadora.push(el)
    })
  }

  @Mutation
  changeConveniosOperadora(value: any) {
    const index = this.conveniosOperadora.findIndex((e: any) => e.convenioId === value.convenioId)
    if (![-1, undefined].includes(index)) {
      this.conveniosOperadora[index] = value
    }
  }

  @Mutation
  clearConveniosOperadora() {
    this.conveniosOperadora = [...this.conveniosOperadoraDefault]
  }

  public convenios: Convenio[] = []
  public operadoras: Operadora[] = []

  get paginationGrupoDefault() {
    return this.paginationOperadoraConvenioDefault
  }

  public paginationOperadoraConvenio: DataTablePagination = { ...this.paginationOperadoraConvenioDefault }

  @Mutation
  setOperadoras(value: Operadora[]) {
    this.operadoras = value
    this.grupoHospitais = [...new Set(value.map((e: any) => e.grupoHospital).filter((e: any) => !['', undefined, null].includes(e)))]
  }

  @Mutation
  clearOperadoras() {
    this.operadoras = []
  }

  @Mutation
  setConvenios(value: Convenio[]) {
    this.convenios = value
  }

  @Mutation
  clearConvenios() {
    this.convenios = []
  }

  @Action
  async getOperadoraConvenio(hospitalId: string) {
    try {
      const request = await Apis.Agendamento.SuporteApi.getOperadoraConvenio(hospitalId)
      this.context.commit('setOperadoras', request.success && request.data ? request.data.operadoras as Operadora[] : [] as Operadora[])
      this.context.commit('setConvenios', request.success && request.data ? request.data.convenio as Convenio[] : [] as Convenio[])
      return request
    } catch (error) {
      this.context.commit('setOperadoras', [] as Operadora[])
      this.context.commit('setConvenios', [] as Convenio[])
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  @Action
  async getConvenioOperadoras(hospitalId: string) {
    try {
      const request = await Apis.Agendamento.SuporteApi.getConvenioOperadoras(hospitalId)
      this.context.commit('setConveniosOperadora', request.success ? request.data as ConvenioOperadora[] : [])
      return request
    } catch (error) {
      this.context.commit('setConveniosOperadora', [])
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  @Action
  async upsertOperadoraConvenio() {
    try {
      const params: any = {
        hospitalId: this.hospitalId,
        data: {
          conveniosOperadoras: this.conveniosOperadora.map((e: ConvenioOperadora) => {
            const obj = { ...e }
            Object.entries(obj).forEach(([key, value]) => {
              if (['', null, undefined].includes(value as any)) {
                obj[key] = null
              }
            })
            return obj
          }).map((e: any) => ({ convenioId: e.convenioId, operadoraId: e.operadoraId })),
        },
      }

      const request = await Apis.Agendamento.SuporteApi.upsertOperadoraConvenio(params)
      return request
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  /** CRIAR OPERADORA */
  operadoraConvenioOperadoraDefault: Operadora = {
    operadoraId: '',
    operadoraNome: '',
    grupoHospital: '',
    registroAns: '',
  }

  operadoraConvenioOperadora: Operadora = { ...this.operadoraConvenioOperadoraDefault }

  @Mutation
  clearOperadoraConvenioOperadora() {
    this.operadoraConvenioOperadora = { ...this.operadoraConvenioOperadoraDefault }
  }

  @Action
  async backofficeRegulacaoInsertOperadora() {
    try {
      const params: any = { ...this.operadoraConvenioOperadora }
      Object.entries(params).forEach(([key, value]) => {
        if (key === 'operadoraNome') {
          params.nome = value
          delete params[key]
        }
        if (['', null, undefined].includes(value as any)) {
          delete params[key]
        }
      })
      const request = await Apis.Agendamento.SuporteApi.backofficeRegulacaoInsertOperadora(params)
      return request
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }
}
